import { Field, useFormikContext } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import DeleteIcon from "../../assets/icons/delete-icon";
import { url } from "../../redux/config";
import SearchAddFeild from "../forms/search-add-field";
import { api } from "../../redux/apis/api";
import { truncateString } from "../helper-components/helper-function";
import {
  purchaseProductDetailsAction,
  purchaseProductListAction,
} from "../../redux/actions/purchase-action";
import SelectField from "../forms/select-field";
import AddButton from "../buttons/button";

const PurchaseItemsSection = ({
  initialData,
  disabled = false,
  searchRef,
  addBatchFn = () => {},
  prefixName = "",
  heading = [],
}) => {
  const inputRefs = useRef(null);
  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const [localData, setLocalData] = useState([]);

  // Recalculate batch GST (SGST/CGST vs IGST) when is_inter_state changes due to address change
  const prevIsInterStateRef = useRef(undefined);
  const isInterStateMountedRef = useRef(false);
  useEffect(() => {
    if (!isInterStateMountedRef.current) {
      isInterStateMountedRef.current = true;
      prevIsInterStateRef.current = values?.is_inter_state;
      return;
    }
    const prev = prevIsInterStateRef.current;
    const curr = values?.is_inter_state;
    if (prev === curr) return;
    prevIsInterStateRef.current = curr;

    if (curr === null || curr === undefined) return;
    const data = values?.data || [];
    const hasBatches = data.some((item) => (item?.item_batches || []).length > 0);
    if (!hasBatches) return;

    const updatedData = data.map((item) => {
      const batches = item?.item_batches || [];
      if (batches.length === 0) return item;

      const updatedBatches = batches.map((batch) => {
        const tax = parseFloat(batch?.item_batch_tax_amount || 0);
        const taxPct = parseFloat(batch?.item_batch_tax_percentage || 0);
        let sgst, cgst, igst, sgstPct, cgstPct, igstPct;
        if (curr === true) {
          igst = parseFloat(tax.toFixed(2));
          igstPct = taxPct;
          sgst = 0; cgst = 0; sgstPct = 0; cgstPct = 0;
        } else {
          sgst = parseFloat((tax / 2).toFixed(2));
          cgst = parseFloat((tax / 2).toFixed(2));
          sgstPct = parseFloat((taxPct / 2).toFixed(2));
          cgstPct = parseFloat((taxPct / 2).toFixed(2));
          igst = 0; igstPct = 0;
        }
        return {
          ...batch,
          item_batch_sgst_amount: sgst,
          item_batch_cgst_amount: cgst,
          item_batch_igst_amount: igst,
          item_batch_sgst_percentage: sgstPct,
          item_batch_cgst_percentage: cgstPct,
          item_batch_igst_percentage: igstPct,
        };
      });

      const totalSGST = updatedBatches.reduce((sum, b) => sum + (parseFloat(b.item_batch_sgst_amount) || 0), 0);
      const totalCGST = updatedBatches.reduce((sum, b) => sum + (parseFloat(b.item_batch_cgst_amount) || 0), 0);
      const totalIGST = updatedBatches.reduce((sum, b) => sum + (parseFloat(b.item_batch_igst_amount) || 0), 0);

      return {
        ...item,
        item_batches: updatedBatches,
        item_sgst: +totalSGST.toFixed(2),
        item_cgst: +totalCGST.toFixed(2),
        item_igst: +totalIGST.toFixed(2),
      };
    });

    setFieldValue("data", updatedData);
  }, [values?.is_inter_state]); // eslint-disable-line react-hooks/exhaustive-deps
  const fetchData = async (inputValue, callback, field) => {
    const onSuccess = (data) => {
      const options =
        data?.data === null
          ? []
          : data.data
              .map((item) => ({
                value: item.item_id,
                label: item[field],
              }))
              .filter(
                (option) =>
                  !values?.data?.some((item) => item?.item_id === option.value)
              );
      setLocalData(data.data);
      callback(options);
    };
    const onError = (data) => {};
    if (inputValue?.length > 0) {
      dispatch(
        purchaseProductListAction(
          {
            field: field,
            value: inputValue,
          },
          onSuccess,
          onError
        )
      );
    }
  };

  const removeRow = (rowIndex, type = "") => {
    let updatedData = [...values.data];

    if (type === "all") {
      updatedData =
        updatedData.length > 0 ? [updatedData[updatedData.length - 1]] : [];
    } else {
      updatedData.splice(rowIndex, 1);
    }
    setFieldValue("data", updatedData);

    const raw_total_amount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_total_amount) || 0);
    }, 0);

    const total_tax = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_tax_amount) || 0);
    }, 0);

    const total_discount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_discount_amount) || 0);
    }, 0);

    const sub_total = (raw_total_amount - total_tax + total_discount).toFixed(
      2
    );

    const total_amount = Math.round(raw_total_amount).toFixed(2);
    const round_off_value = (
      Math.round(raw_total_amount) - raw_total_amount
    ).toFixed(2);

    // Set all values to form
    setFieldValue(`${prefixName}_sub_total`, sub_total);
    setFieldValue(`${prefixName}_total_gst`, total_tax);
    setFieldValue(`${prefixName}_total_discount`, total_discount);
    setFieldValue(`${prefixName}_total_amount`, total_amount);
    setFieldValue(`${prefixName}_round_off_value`, round_off_value);
    setFieldValue(`${prefixName}_tcs_or_tds_amount`, 0);
    setFieldValue(`${prefixName}_tcs_or_tds_percentage_id`, "");
    setFieldValue(`${prefixName}_tcs_or_tds_percentage`, 0);
    setFieldValue(`${prefixName}_tds_tcs_type`, "");
  };

  const onErrorDis = (data) => {};

  const handleQuantityChange = async (event, rowIndex) => {
    const updatedData = values.data.map((row, index) =>
      index === rowIndex
        ? {
            ...row,
            quantity: parseFloat(event.target.value),
            item_pack_size:
              (parseFloat(row?.base_quantity) || 1) *
              (parseFloat(event.target.value) +
                parseFloat(row?.item_free_quantity || 0)),
          }
        : row
    );
    setFieldValue("data", updatedData);
    const totalAmount =
      parseFloat(values?.data?.[rowIndex]?.item_unit_price) *
      parseFloat(event.target.value);
    if (parseFloat(event?.target?.value) > 0) {
      try {
        const response = await api.get(
          `${url}/discount/get?totalAmountOfItem=${totalAmount}&item_quantity=${event?.target?.value}&item_id=${values?.data?.[rowIndex]?.item_id}&cmr_phone_number=""`,
          JSON.stringify(totalAmount)
        );
        const tax =
          response.data?.code === 200 ? response.data.data.item_tax_amount : 0;
        const updatedDataRow = {
          ...updatedData[rowIndex],
          item_gst:
            response.data?.code === 200
              ? response.data.data.item_total_tax_percentage
              : 0,
          item_sgst:
            response.data?.code === 200 ? (parseFloat(tax) / 2).toFixed(2) : 0,
          item_cgst:
            response.data?.code === 200 ? (parseFloat(tax) / 2).toFixed(2) : 0,
          item_igst:
            response.data?.code === 200
              ? (
                  values?.data?.[rowIndex]?.quantity *
                  parseFloat(values?.data?.[rowIndex]?.item_unit_price) *
                  (response.data.data.igstRate / 100)
                )?.toFixed(2)
              : 0,

          item_total_amount:
            parseFloat(values?.data?.[rowIndex]?.item_unit_price) *
              parseFloat(values?.data?.[rowIndex]?.quantity) +
            parseFloat(tax),
          item_tax_amount: parseFloat(tax),
        };
        updatedData[rowIndex] = updatedDataRow;

        setFieldValue("data", [...updatedData]);
      } catch (error) {
        onErrorDis(error);
      }
    }
  };

  const hasDuplicateItemId = (itemId, currentIndex) => {
    const itemIdsExceptCurrent = values.data
      .filter((_, index) => index !== currentIndex)
      .map((item) => item.item_id);

    return itemIdsExceptCurrent.includes(itemId);
  };

  const onSuccessDetails = (data, rowIndex, filteredData) => {
    const oldData = [...values?.data];
    oldData.pop();
    const base_quantity = data?.data?.uom_dropdown
      ? (
          data?.data?.uom_dropdown?.find(
            (uom) => uom?.item_id === filteredData?.item_uom_id
          ) ||
          data?.data?.uom_dropdown?.find(
            (uom) => uom?.alt_uom === filteredData?.item_uom
          )
        )?.base_quantity ?? 1
      : 1;
    const updatedData = {
      ...filteredData,
      base_quantity,
      item_pack_size: 0,
      uom_dropdown: data?.data?.uom_dropdown
        ? data?.data?.uom_dropdown?.map((uom) => ({
            item_uom_id: uom?.item_id,
            label: uom?.alt_uom,
            value: uom?.alt_uom,
            base_quantity: uom?.base_quantity,
          }))
        : [],
    };

    setFieldValue("data", [...oldData, updatedData, initialData]);
    document.querySelector(`[name="data[${rowIndex}].item_uom"]`)?.focus();

    inputRefs.current?.focus();
  };

  const onErrorDetails = (data) => {};

  return (
    <>
      <div
        style={{
          maxHeight: `calc(100% - 8.5rem)`, // Limit the container height
          height: "auto", // Let it grow naturally to fit its content
        }}
        className={`border border-[var(--create-table-main-border)] border-1 border-radius p-3`}
      >
        <div
          style={{
            maxHeight: `100%`,
            minHeight: `5rem`,
          }}
          className={`tab-container border-radius ${
            disabled ? "mt-[1rem]" : ""
          }`}
        >
          <table className="w-full border-collapse mt-0">
            <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
              <tr className="relative h-[50px]">
                {heading
                  ?.filter((heading) => heading?.enabled !== false)
                  ?.map((heading, index) => (
                    <th
                      style={{ minWidth: `${heading?.minWidth}px` }}
                      key={index}
                      className={`px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]`}
                    >
                      {heading?.value === "clear_all" ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent:
                              heading?.alignment === "right"
                                ? "flex-end"
                                : heading?.alignment === "center"
                                ? "center"
                                : "flex-start",
                            width: "100%", // Ensure the div takes full width of the th
                          }}
                        >
                          {!disabled && values?.data?.length > 1 && (
                            <AddButton
                              type="button"
                              onclkFn={() => {
                                if (values?.data?.length > 0) {
                                  removeRow(0, "all");
                                }
                              }}
                              prefixIcon={false}
                              text={"Clear All"}
                              className="flex items-center px-[10px] py-[10px] h-[20px] whitespace-nowrap border-radius gap-[8px]
                 bg-[var(--create-table-clearAll-bg)] 
                 cursor-pointer 
                 text-[var(--create-table-clearAll-color)] 
                 text-[length:var(--create-table-clearAll-fs)] 
                 font-[var(--create-table-clearAll-fw)] 
                 border-2 border-[var(--create-table-clearAll-border)] 
                 hover:bg-[var(--create-table-clearAll-bg)]"
                            />
                          )}
                        </div>
                      ) : heading?.childrenHeading?.length > 0 ? (
                        <div className="flex gap-3">
                          {heading?.childrenHeading?.map(
                            (childHeading, childIndex) => (
                              <div
                                style={{
                                  minWidth: `90px`,
                                  width: `${childHeading?.minWidth}px`,
                                  textAlign: childHeading?.alignment, // Keep textAlign for normal text cells
                                }}
                                className="bg-[var(--create-table-batch-con-bg)] px-3 py-1 text-[var(--create-table-batch-con-fg)] 
                              text-[length:var(--create-table-batch-con-fs)] 
                              font-[var(---create-table-batch-con-fw)] rounded-[8px]"
                              >
                                {childHeading?.label}
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <div
                          className="flex items-center gap-2 w-full"
                          style={{
                            justifyContent:
                              heading?.alignment === "right"
                                ? "flex-end"
                                : heading?.alignment === "center"
                                ? "center"
                                : "flex-start",
                          }}
                        >
                          {heading?.label}
                        </div>
                      )}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="h-full">
              {values?.data?.map((rowData, rowIndex) => (
                <tr
                  className={`  bg-[var(--create-table-row-bg)]   h-[50px] `}
                  key={rowIndex}
                >
                  {heading
                    ?.filter((heading) => heading?.enabled !== false)
                    ?.map((heading, index) => {
                      return (
                        <td
                          style={{
                            minWidth: `${heading?.minWidth}px`,
                            textAlign: heading?.alignment, // Keep textAlign for normal text cells
                          }}
                          key={index}
                          className="border-b border-[var(--create-table-border)] px-[8px] text-left text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap cursor-pointer"
                        >
                          {heading.value === "item_name" ||
                          heading.value === "item_code" ||
                          heading.value === "item_generic_name" ? (
                            <div title={rowData[heading.value]}>
                              <SearchAddFeild
                                menuWidth="35rem"
                                searchRef={
                                  heading.value === "item_name" ? searchRef : {}
                                }
                                isDisabled={
                                  values?.data?.length - 1 !== rowIndex ||
                                  disabled
                                    ? true
                                    : false
                                }
                                errorClass="hidden"
                                isRequired={false}
                                value={{
                                  label: rowData[heading.value],
                                  value: "",
                                }}
                                name={`data[${rowIndex}].${heading.value}`}
                                field={heading.value}
                                isLabelReq={false}
                                defaultSelectClass=""
                                placeholder=""
                                fetchData={fetchData}
                                onChange={async (selectedOption) => {
                                  const filteredData = localData
                                    ?.filter(
                                      (item) =>
                                        item?.item_id === selectedOption?.value
                                    )
                                    .map((item) => ({
                                      sl_no: 1,
                                      item_name: item.item_name,
                                      item_generic_name:
                                        item?.item_generic_name,
                                      item_code: item.item_code,
                                      item_hsn: item.item_hsn,
                                      item_manufacturer_name:
                                        item.item_manufacturer_name,
                                      item_uom:
                                        item.item_pur_purchasing_uom_name ===
                                        null
                                          ? ""
                                          : item.item_pur_purchasing_uom_name,
                                      item_batches: [],
                                      // item_unit_price: item?.item_unit_price,
                                      // item_price_wiithout_tax:0,
                                      item_to_be_received: 0,
                                      quantity: 0,
                                      item_pack_size: 0,
                                      item_free_quantity: 0,
                                      item_discount_amount: 0,
                                      item_tax_amount: 0,
                                      item_total_amount: 0,
                                      item_id: item?.item_id,
                                      item_uom_id:
                                        item?.item_pur_purchasing_uom_name_id ??
                                        "",
                                      uom_dropdown: [],

                                      item_manufacturer_id:
                                        item.item_manufacturer,
                                      uom_group_id: item?.item_uom || "",
                                    }));

                                  dispatch(
                                    purchaseProductDetailsAction(
                                      {
                                        item_id: filteredData?.[0]?.item_id,
                                        item_uom:
                                          filteredData?.[0]?.uom_group_id,
                                      },
                                      (data) => {
                                        onSuccessDetails(
                                          data,
                                          rowIndex,
                                          ...filteredData
                                        );
                                      },
                                      onErrorDetails
                                    )
                                  );
                                }}
                              />
                            </div>
                          ) : heading.value === "quantity" ? (
                            <div className="flex">
                              <Field name={`data[${rowIndex}].quantity`}>
                                {({ field }) => (
                                  <input
                                    min={0}
                                    disabled={true}
                                    {...field}
                                    type="number"
                                    step="1"
                                    placeholder=""
                                    readOnly={
                                      values?.data?.length - 1 === rowIndex ||
                                      hasDuplicateItemId(
                                        rowData.item_id,
                                        rowIndex
                                      ) ||
                                      disabled
                                    }
                                    onKeyDown={(event) => {
                                      if (
                                        event.key === "-" ||
                                        event.key === "e" ||
                                        event.key === "."
                                      ) {
                                        event.preventDefault();
                                      }
                                      if (
                                        event.key === "Tab" ||
                                        event.key === "Enter"
                                      ) {
                                        if (searchRef && searchRef.current) {
                                          setTimeout(() => {
                                            searchRef.current.focus();
                                          }, 0);
                                        }
                                      }
                                    }}
                                    onBlur={(event) => {
                                      if (
                                        values?.data?.length - 1 === rowIndex ||
                                        hasDuplicateItemId(
                                          rowData.item_id,
                                          rowIndex
                                        ) ||
                                        disabled
                                      ) {
                                        event?.preventDefault();
                                      } else {
                                        handleQuantityChange(event, rowIndex);
                                      }
                                    }}
                                    className="focus-within:ring-2  w-full  rounded  input-field-no-spin-buttons"
                                  />
                                )}
                              </Field>
                            </div>
                          ) : heading.value === "sl_no" ? (
                            <div>
                              <input
                                type="radio"
                                name="SerialNumberRadioGroup"
                                disabled={disabled}
                                value={rowIndex}
                                className="peer hidden"
                                id={rowIndex}
                              />
                              <label
                                htmlFor={rowIndex}
                                className="block cursor-pointer select-none rounded-xl p-3 text-center hover:bg-slate-100 peer-checked:bg-blue-100 peer-checked:font-bold peer-checked:outline-none peer-checked:ring-2 peer-checked:ring-blue-500"
                              >
                                {rowIndex + 1}
                              </label>
                            </div>
                          ) : heading.value === "item_manufacturer_name" ? (
                            <p title={rowData[heading.value]}>
                              {truncateString(rowData[heading.value] ?? "", 6)}
                            </p>
                          ) : heading.value === "item_batches" ? (
                            <div className="py-1">
                              {rowIndex < values?.data?.length - 1 &&
                                rowData.item_batches?.length === 0 && (
                                  <button
                                    autoFocus={true}
                                    onClick={() => addBatchFn(rowData?.item_id)}
                                    type="button"
                                    className="text-[var(--create-table-row-placeholder-color)]  text-center w-full"
                                  >
                                    {disabled ? "View" : "Click to add"}
                                  </button>
                                )}
                              {Array.isArray(rowData.item_batches) &&
                                rowData.item_batches.map((batch, index) => (
                                  <div
                                    onClick={() => addBatchFn(rowData?.item_id)}
                                    className="flex items-center gap-3 mt-[0.5rem]"
                                  >
                                    {heading?.childrenHeading
                                      ?.filter(
                                        (heading) => heading?.enabled !== false
                                      )
                                      ?.map((heading, index) => (
                                        <div
                                      style={{
                                        minWidth: `90px`,
                                        width: `${heading?.minWidth}px`,
                                        textAlign: heading?.alignment,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                      }}
                                      className="bg-[var(--create-table-batch-con-bg)] px-2 py-1 text-[var(--create-table-batch-con-fg)] 
                                                text-[length:var(--create-table-batch-con-fs)] 
                                                font-[var(---create-table-batch-con-fw)] rounded-[8px]"
                                    >
                                      {batch[heading.value] || "-"}
                                    </div>
                                      ))}
                                  </div>
                                ))}
                            </div>
                          ) : heading.value === "item_uom" ? (
                            <>
                              <SelectField
                                style={customStyles}
                                defaultInputClass="min-h-[42px] min-w-[8rem] rounded-[8px]  text-[var(--create-table-row-fg-color)] text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] input-field-no-spin-buttons placeholder:text-[var(--create-table-row-placeholder-color)] placeholder:text-[length:var(--create-table-batch-con-fs)] placeholder:font-[var(--create-table-row-font-weight)]  "
                                inputBorder=""
                                general_color={false}
                                handleSelected={(sel) => {
                                  setFieldValue(
                                    `data[${rowIndex}].item_uom_id`,
                                    sel?.item_uom_id
                                  );
                                  setFieldValue(
                                    `data[${rowIndex}].base_quantity`,
                                    sel?.base_quantity
                                  );

                                  setFieldValue(
                                    `data[${rowIndex}].item_pack_size`,
                                    (parseFloat(sel?.base_quantity) || 1) *
                                      (parseInt(rowData?.quantity) +
                                        parseInt(
                                          rowData?.item_free_quantity || 0
                                        ))
                                  );

                                  console.log(
                                    `data[${rowIndex}].item_pack_size`,
                                    sel?.base_quantity,
                                    parseInt(rowData?.quantity),
                                    parseInt(rowData?.item_free_quantity || 0),
                                    sel,
                                    "gdggdgdgdgdgdfgddg"
                                  );
                                }}
                                borderclass=""
                                defaultSelectClass="border-0"
                                selectClass="w-full"
                                defaultValue={
                                  values.data[rowIndex]?.item_uom
                                    ? {
                                        label:
                                          values.data[rowIndex]?.item_uom ?? "",
                                      }
                                    : null
                                }
                                isDisabled={disabled}
                                errMsgContainerClass="hidden"
                                id={`data[${rowIndex}].item_uom`}
                                isContainer={false}
                                options={values.data[rowIndex]?.uom_dropdown}
                                name={`data[${rowIndex}].item_uom`}
                                placeholder={"Select UOM"}
                              />
                            </>
                          ) : heading.value === "item_free_quantity" &&
                            rowData.item_batches?.length > 0 ? (
                            rowData?.item_batches?.reduce(
                              (sum, batch) =>
                                sum +
                                (parseInt(batch.item_batch_free_quantity, 10) ||
                                  0),
                              0
                            )
                          ) : heading.value === "item_pack_size" ? (
                            rowData?.item_pack_size || 0
                          ) : heading.value === "clear_all" ? (
                            <button
                              type="button"
                              onClick={() => {
                                removeRow(rowIndex, "");
                              }}
                              style={{
                                display: "flex",
                                justifyContent:
                                  heading?.alignment === "right"
                                    ? "flex-end"
                                    : heading?.alignment === "center"
                                    ? "center"
                                    : "flex-start",
                                width: "100%", // Take full width of the td
                              }}
                            >
                              {!disabled &&
                                rowIndex !== values?.data?.length - 1 && (
                                  <DeleteIcon className="cursor-pointer text-red-500" />
                                )}
                            </button>
                          ) : heading.value === "item_to_be_received" ? (
                            <div>{rowData?.item_return_open_quantity}</div>
                          ): (
                            rowData[heading.value]
                          )}
                        </td>
                      );
                    })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default PurchaseItemsSection;

const customStyles = {
  indicatorSeparator: () => ({ display: "none" }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? "transparent" : "transparent",
    boxShadow: state.isFocused ? null : null,
    "&:hover": {},
    fontFamily: "Inter,sans-serif ",
    color: "var(--create-table-row-fg-color)",
    fontFamily: "Inter,sans-serif",
    fontWeight: "var(--create-table-row-font-weight)",
    fontSize: "var(--create-table-row-font-size)",
    borderRadius: "10px",
    width: "100%",
    backgroundColor: state.isDisabled ? "inherit" : base.backgroundColor,
    borderColor: state.isFocused ? "transparent" : "transparent",
    boxShadow: state.isFocused ? null : null,
    "&:hover": {},
    height: "100%",
  }),
  option: (provided, state) => {
    const defaultBackgroundColor = "white"; // Default background color
    const focusedBackgroundColor = "#0000002E"; // Background color when focused
    const selectedBackgroundColor = "#e5ecffb2"; // Background color when selected

    return {
      ...provided,
      ...(state.data.color ? { color: state.data.color } : { color: "black" }),
      // backgroundColor: state?.isSelected ? "red" : "red",
      backgroundColor: state.isSelected
        ? selectedBackgroundColor // Use selected background color
        : state.isFocused
        ? focusedBackgroundColor // Use focused background color
        : defaultBackgroundColor, // Use default background color

      fontSize: "14px",
      zIndex: "999",
      borderBottom: "1px solid #F5F5F5",
      fontFamily: "Inter, sans-serif",
      "&:hover": {
        backgroundColor: state.isFocused
          ? focusedBackgroundColor
          : focusedBackgroundColor, // Keeps hover state consistent
      },
    };
  },
  placeholder: (defaultStyles) => {
    return {
      ...defaultStyles,
      color: "var(--placeholder-color)",
      fontFamily: "Inter,sans-serif",
      // fontSize: "14px",
      fontWeight: "400",
      opacity: "1",
      // position: "absolute",
      // top: "30%",
      whiteSpace: "nowrap",
    };
  },
  multiValue: (base) => ({
    ...base,
    fontFamily: "Inter,sans-serif ",
    backgroundColor: "#E5ECFF",
    gap: "5px",
    padding: "0px 6px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--create-table-row-fg-color)",
    fontFamily: "Inter,sans-serif",
    fontWeight: "var(--create-table-row-font-weight)",
    fontSize: "var(--create-table-row-font-size)",
  }),

  dropdownIndicator: (provided, state) => ({
    ...provided,
    ...(state.selectProps.menuIsOpen
      ? {
          transform: "rotate(180deg)",
        }
      : {}),
  }),
};
